import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, Stack, Typography } from "@mui/material";
import useMarkNotificationRead from "hooks/api/REST/notifications/useMarkNotificationRead";
import { INotification } from "models/notificationItem.models";
import { getIsEmptyString, getRelativeTimeString } from "utils/helpers";
import CallToAction from "./CallToAction";
import {
  getDisplayedNotificationStatus,
  getNotificationIcon,
  getNotificationTitleForReason,
  getReadableAssetType,
} from "./NotificationsListItem.utils";
import { NewIndicatorIcon } from "assets/icons";
import styles from "./NotificationsListItem.styles";

type TNotificationsListItemProps = {
  data: INotification;
};

const NotificationsListItem = ({ data }: TNotificationsListItemProps) => {
  const { id, templateType, metadata, createdDate, read: isRead } = data;

  const {
    templateText,
    ctaType,
    assetType,
    currentStatus,
    targetStatus,
    previousStatus,
  } = metadata;

  const containerStyle = useMemo(
    () => ({
      ...styles.container,
      // append extra css for unread notification item
      ...(!isRead && styles.unread),
    }),
    [isRead],
  );

  const { t, i18n } = useTranslation("common");

  const { triggerMarkNotificationAsRead } = useMarkNotificationRead();

  const onMarkAsRead = () => {
    if (isRead) {
      return;
    }

    triggerMarkNotificationAsRead({
      id,
      notification: { ...data, read: true },
    });
  };

  return (
    <Stack
      direction="row"
      aria-label={templateType}
      onClick={onMarkAsRead}
      sx={containerStyle}
    >
      <Box sx={styles.iconParentContainer} data-qaid="notification-item-icon">
        {!isRead && (
          <Box
            sx={styles.newIndicator}
            data-qaid="notification-item-icon-new-indicator"
          >
            <NewIndicatorIcon sx={{ fontSize: 8 }} />
          </Box>
        )}
        <Box sx={styles.iconContainer}>{getNotificationIcon(ctaType)}</Box>
      </Box>
      <Stack
        direction="column"
        sx={{
          gap: 1.5,
        }}
      >
        <Stack direction="column" sx={styles.contentContainer}>
          {!getIsEmptyString(templateText) && (
            <Typography
              variant="body2"
              aria-label="content"
              sx={styles.templateText}
              data-qaid="notification-item-text-content"
            >
              {t(templateText, {
                ...metadata,
                assetType: getReadableAssetType(assetType, t),
                ...(targetStatus && {
                  targetStatus: getDisplayedNotificationStatus(
                    ctaType,
                    targetStatus,
                    t,
                  ),
                }),
                ...(previousStatus && {
                  previousStatus: getDisplayedNotificationStatus(
                    ctaType,
                    previousStatus,
                    t,
                  ),
                }),
                ...(currentStatus && {
                  currentStatus: getDisplayedNotificationStatus(
                    ctaType,
                    currentStatus,
                    t,
                  ),
                }),
              })}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={styles.notificationCaption}
            data-qaid="notification-item-creation-time"
          >
            {createdDate &&
              getRelativeTimeString(new Date(createdDate), i18n.language)}
          </Typography>
        </Stack>
        {!!data?.metadata?.reason && (
          <Stack
            direction="column"
            sx={{
              gap: 1,
            }}
          >
            <Divider style={{ width: "50%" }} />
            <Box>
              <Typography variant="caption" sx={styles.notificationCaption}>
                {getNotificationTitleForReason(data, t)}
              </Typography>
              <Typography variant="body2">{data?.metadata?.reason}</Typography>
            </Box>
          </Stack>
        )}
        <CallToAction data={data} />
      </Stack>
    </Stack>
  );
};

export default NotificationsListItem;
